import React, { useState, useEffect, useCallback } from 'react';
import { MdAdd, MdSearch, MdDevices, MdEdit, MdClose, MdUploadFile, MdFileDownload } from 'react-icons/md';
import API from '../api';
import { useAuth } from '../hooks/useAuth';
import { translateUiText, useLanguage } from '../i18n';
import { useSiteSettings, formatMoney } from '../hooks/useSiteSettings';

const STATUS_OPTIONS = ['active', 'inactive', 'under_repair', 'disposed', 'lost'];
const CONDITION_OPTIONS = ['excellent', 'good', 'fair', 'poor'];

function Modal({ title, onClose, children }) {
    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-box" onClick={e => e.stopPropagation()}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid #e2e8f0' }}>
                    <h6 style={{ margin: 0, fontWeight: 600 }}>{title}</h6>
                    <button onClick={onClose} style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#64748b', padding: 4 }}><MdClose size={20} /></button>
                </div>
                <div style={{ padding: 20 }}>{children}</div>
            </div>
        </div>
    );
}

const emptyForm = {
    asset_tag: '', name: '', category_id: '', building_id: '', space_id: '',
    assigned_to: '', purchase_date: '', purchase_cost: '', current_value: '',
    warranty_expiry: '', manufacturer: '', model: '', serial_number: '',
    status: 'active', condition_rating: 'good', notes: ''
};

export default function Assets() {
    const { isManager, assignedFacility } = useAuth();
    const { currency } = useSiteSettings();
    const { t } = useLanguage();
    const RWF = n => formatMoney(n, currency);
    const [assets, setAssets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [filterStatus, setFilterStatus] = useState('');
    const [filterCat, setFilterCat] = useState('');
    const [categories, setCategories] = useState([]);
    const [buildings, setBuildings] = useState([]);
    const [users, setUsers] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [form, setForm] = useState(emptyForm);
    const [editId, setEditId] = useState(null);
    const [saving, setSaving] = useState(false);
    const [stats, setStats] = useState(null);
    const [spaces, setSpaces] = useState([]);
    const [showImport, setShowImport] = useState(false);
    const [importFile, setImportFile] = useState(null);
    const [importBusy, setImportBusy] = useState(false);
    const [importResult, setImportResult] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = {};
            if (search) params.search = search;
            if (filterStatus) params.status = filterStatus;
            if (filterCat) params.category_id = filterCat;
            const [assetsRes, statsRes] = await Promise.all([
                API.get('/assets', { params }),
                API.get('/assets/stats')
            ]);
            setAssets(assetsRes.data);
            setStats(statsRes.data);
        } finally { setLoading(false); }
    }, [search, filterStatus, filterCat]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        API.get('/assets/categories').then(r => setCategories(r.data));
        API.get('/spaces/buildings').then(r => setBuildings(r.data));
        API.get('/auth/users/list').then(r => setUsers(r.data.filter(u => u.role !== 'developer')));
    }, []);

    // Rooms / locations of the building chosen in the form (store, parking, office...).
    useEffect(() => {
        if (!showModal || !form.building_id) { setSpaces([]); return; }
        let cancelled = false;
        API.get('/spaces', { params: { building_id: form.building_id } })
            .then(r => { if (!cancelled) setSpaces(r.data); })
            .catch(() => { if (!cancelled) setSpaces([]); });
        return () => { cancelled = true; };
    }, [showModal, form.building_id]);

    const downloadTemplate = async () => {
        try {
            const response = await API.get('/assets/import/template', { responseType: 'blob' });
            const url = URL.createObjectURL(response.data);
            const link = document.createElement('a'); link.href = url; link.download = 'assets_import_template.csv'; link.click(); URL.revokeObjectURL(url);
        } catch (err) { alert(err.response?.data?.error || t('assets.importFailed')); }
    };
    const runImport = async e => {
        e.preventDefault();
        if (!importFile) { setImportResult({ error: t('assets.importNoFile') }); return; }
        setImportBusy(true); setImportResult(null);
        try {
            const formData = new FormData(); formData.append('file', importFile);
            const { data } = await API.post('/assets/import', formData);
            setImportResult({ ok: true, ...data });
            setImportFile(null);
            load();
        } catch (err) {
            const data = err.response?.data || {};
            setImportResult({ error: data.error || t('assets.importFailed'), errors: data.errors || [], error_count: data.error_count || 0 });
        } finally { setImportBusy(false); }
    };

    const openCreate = () => {
        const defaultForm = { ...emptyForm };
        if (isManager && assignedFacility) defaultForm.building_id = String(assignedFacility.id);
        setForm(defaultForm); setEditId(null); setShowModal(true);
    };
    const openEdit = a => { setForm({ ...emptyForm, ...a }); setEditId(a.id); setShowModal(true); };

    const handleSave = async e => {
        e.preventDefault();
        setSaving(true);
        try {
            if (editId) await API.put(`/assets/${editId}`, form);
            else await API.post('/assets', form);
            setShowModal(false);
            load();
        } catch (err) {
            alert(err.response?.data?.error || translateUiText('Save failed'));
        } finally { setSaving(false); }
    };

    const conditionColor = { excellent: '#15803d', good: '#0369a1', fair: '#b45309', poor: '#b91c1c' };
    const STATUS_LABELS = {
        active: t('assets.statusActive'), inactive: t('assets.statusInactive'), under_repair: t('assets.statusUnderRepair'),
        disposed: t('assets.statusDisposed'), lost: t('assets.statusLost')
    };
    const CONDITION_LABELS = { excellent: t('assets.conditionExcellent'), good: t('assets.conditionGood'), fair: t('assets.conditionFair'), poor: t('assets.conditionPoor') };
    const statusLabel = s => STATUS_LABELS[s] || s;
    const conditionLabel = c => CONDITION_LABELS[c] || c;

    return (
        <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
                <div>
                    <h4 style={{ margin: 0, fontWeight: 700 }}>{t('assets.pageTitle')}</h4>
                    <p style={{ margin: 0, color: '#64748b', fontSize: 13 }}>
                        {isManager && assignedFacility
                            ? `${assignedFacility.name} ${t('assets.pageSubtitleFacility')}`
                            : t('assets.pageSubtitle')}
                    </p>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                    <button className="btn btn-outline-primary" onClick={() => { setImportResult(null); setImportFile(null); setShowImport(true); }} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <MdUploadFile size={18} /> {t('assets.importButton')}
                    </button>
                    <button className="btn btn-primary" onClick={openCreate} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <MdAdd size={18} /> {t('assets.addAsset')}
                    </button>
                </div>
            </div>

            {/* Stats */}
            {stats && (
                <div className="row g-3 mb-4">
                    <div className="col-md-3 col-6">
                        <div className="stat-card">
                            <div className="stat-icon blue"><MdDevices /></div>
                            <div><div className="stat-value">{stats.total}</div><div className="stat-label">{t('assets.statTotalAssets')}</div></div>
                        </div>
                    </div>
                    {stats.by_status?.map(s => (
                        <div className="col-md-3 col-6" key={s.status}>
                            <div className="stat-card">
                                <div className={`stat-icon ${s.status === 'active' ? 'green' : s.status === 'under_repair' ? 'yellow' : 'red'}`}><MdDevices /></div>
                                <div><div className="stat-value">{s.cnt}</div><div className="stat-label">{statusLabel(s.status)}</div></div>
                            </div>
                        </div>
                    ))}
                    <div className="col-md-3 col-6">
                        <div className="stat-card">
                            <div className="stat-icon teal"><MdDevices /></div>
                            <div><div className="stat-value">{RWF(stats.total_value)}</div><div className="stat-label">{t('assets.statTotalValue')}</div></div>
                        </div>
                    </div>
                </div>
            )}

            {/* Filters */}
            <div className="page-card mb-4">
                <div style={{ padding: '14px 16px', display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                    <div style={{ position: 'relative', flex: 1, minWidth: 220 }}>
                        <MdSearch style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
                        <input className="form-control" style={{ paddingLeft: 34 }} placeholder={t('assets.searchPlaceholder')}
                            value={search} onChange={e => setSearch(e.target.value)} />
                    </div>
                    <select className="form-select" style={{ width: 140 }} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
                        <option value="">{t('assets.filterAllStatus')}</option>
                        {STATUS_OPTIONS.map(s => <option key={s} value={s}>{statusLabel(s)}</option>)}
                    </select>
                    <select className="form-select" style={{ width: 160 }} value={filterCat} onChange={e => setFilterCat(e.target.value)}>
                        <option value="">{t('assets.filterAllCategories')}</option>
                        {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                </div>
            </div>

            {/* Table */}
            <div className="page-card">
                <div style={{ overflowX: 'auto' }}>
                    {loading ? (
                        <div className="spinner-wrap"><div className="spinner-border" /></div>
                    ) : (
                        <table className="fms-table">
                            <thead>
                                <tr>
                                    <th>{t('assets.colTag')}</th><th>{t('assets.colName')}</th><th>{t('assets.colCategory')}</th><th>{t('assets.colLocation')}</th>
                                    <th>{t('assets.colStatus')}</th><th>{t('assets.colCondition')}</th><th>{t('assets.colPurchaseDate')}</th><th>{t('assets.colCurrentValue')}</th><th>{t('assets.colActions')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {assets.map(a => (
                                    <tr key={a.id}>
                                        <td><span style={{ fontFamily: 'monospace', fontSize: 12, color: '#0ea5e9', fontWeight: 600 }}>{a.asset_tag}</span></td>
                                        <td>
                                            <div style={{ fontWeight: 500 }}>{a.name}</div>
                                            {a.manufacturer && <div style={{ fontSize: 11, color: '#94a3b8' }}>{a.manufacturer} {a.model}</div>}
                                        </td>
                                        <td style={{ fontSize: 13 }}>{a.category_name || '—'}</td>
                                        <td style={{ fontSize: 12 }}>
                                            {a.building_name && <div>{a.building_name}</div>}
                                            {a.space_name && <div style={{ color: '#94a3b8' }}>{a.space_name}</div>}
                                        </td>
                                        <td><span className={`badge-status ${a.status}`}>{statusLabel(a.status)}</span></td>
                                        <td>
                                            <span style={{ fontSize: 12, fontWeight: 500, color: conditionColor[a.condition_rating] || '#64748b', textTransform: 'capitalize' }}>
                                                {conditionLabel(a.condition_rating)}
                                            </span>
                                        </td>
                                        <td style={{ fontSize: 12 }}>{a.purchase_date || '—'}</td>
                                        <td style={{ fontSize: 13, fontWeight: 600 }}>
                                            {a.current_value ? RWF(a.current_value) : '—'}
                                        </td>
                                        <td>
                                            <div style={{ display: 'flex', gap: 6 }}>
                                                <button className="btn btn-sm btn-light" onClick={() => openEdit(a)}><MdEdit size={15} /></button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                {!assets.length && (
                                    <tr><td colSpan={9} style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>
                                        <MdDevices size={32} style={{ marginBottom: 8, display: 'block', margin: '0 auto 8px' }} />{t('assets.noAssetsFound')}
                                    </td></tr>
                                )}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            {/* Modal */}
            {showModal && (
                <Modal title={editId ? t('assets.modalEditTitle') : t('assets.modalAddTitle')} onClose={() => setShowModal(false)}>
                    <form onSubmit={handleSave}>
                        <div className="row g-3">
                            <div className="col-md-6">
                                <label className="form-label fw-semibold">{t('assets.fieldAssetTag')}</label>
                                <input className="form-control" value={form.asset_tag} onChange={e => setForm({ ...form, asset_tag: e.target.value })} disabled={!!editId} placeholder={t('assets.assetTagPlaceholder')} />
                            </div>
                            <div className="col-md-6">
                                <label className="form-label fw-semibold">{t('assets.fieldName')}</label>
                                <input className="form-control" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required />
                            </div>
                            <div className="col-md-6">
                                <label className="form-label fw-semibold">{t('assets.fieldCategory')}</label>
                                <select className="form-select" value={form.category_id} onChange={e => setForm({ ...form, category_id: e.target.value })}>
                                    <option value="">{t('assets.selectCategory')}</option>
                                    {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                                </select>
                            </div>
                            <div className="col-md-6">
                                <label className="form-label fw-semibold">{t('assets.fieldStatus')}</label>
                                <select className="form-select" value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
                                    {STATUS_OPTIONS.map(s => <option key={s} value={s}>{statusLabel(s)}</option>)}
                                </select>
                            </div>
                            <div className="col-md-6">
                                <label className="form-label fw-semibold">{t('assets.fieldBuilding')}</label>
                                {isManager && assignedFacility ? (
                                    <input className="form-control" value={assignedFacility.name} readOnly
                                        style={{ background: '#f8fafc', color: '#64748b' }} />
                                ) : (
                                    <select className="form-select" value={form.building_id || ''} onChange={e => setForm({ ...form, building_id: e.target.value, space_id: '' })}>
                                        <option value="">{t('assets.selectBuilding')}</option>
                                        {buildings.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                                    </select>
                                )}
                            </div>
                            <div className="col-md-6">
                                <label className="form-label fw-semibold">{t('assets.fieldSpace')}</label>
                                <select className="form-select" value={form.space_id || ''} disabled={!form.building_id}
                                    onChange={e => setForm({ ...form, space_id: e.target.value })}>
                                    <option value="">{form.building_id ? t('assets.selectSpace') : t('assets.selectBuildingFirst')}</option>
                                    {spaces.map(sp => <option key={sp.id} value={sp.id}>{sp.name}{sp.room_number ? ` (${sp.room_number})` : ''}</option>)}
                                </select>
                                <div style={{ fontSize: 11.5, color: '#94a3b8', marginTop: 3 }}>{t('assets.spaceHint')}</div>
                            </div>
                            <div className="col-md-6">
                                <label className="form-label fw-semibold">{t('assets.fieldAssignedTo')}</label>
                                <select className="form-select" value={form.assigned_to} onChange={e => setForm({ ...form, assigned_to: e.target.value })}>
                                    <option value="">{t('assets.unassigned')}</option>
                                    {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                                </select>
                            </div>
                            <div className="col-md-6">
                                <label className="form-label fw-semibold">{t('assets.fieldManufacturer')}</label>
                                <input className="form-control" value={form.manufacturer} onChange={e => setForm({ ...form, manufacturer: e.target.value })} />
                            </div>
                            <div className="col-md-6">
                                <label className="form-label fw-semibold">{t('assets.fieldModel')}</label>
                                <input className="form-control" value={form.model} onChange={e => setForm({ ...form, model: e.target.value })} />
                            </div>
                            <div className="col-md-6">
                                <label className="form-label fw-semibold">{t('assets.fieldSerialNumber')}</label>
                                <input className="form-control" value={form.serial_number} onChange={e => setForm({ ...form, serial_number: e.target.value })} />
                            </div>
                            <div className="col-md-6">
                                <label className="form-label fw-semibold">{t('assets.fieldCondition')}</label>
                                <select className="form-select" value={form.condition_rating} onChange={e => setForm({ ...form, condition_rating: e.target.value })}>
                                    {CONDITION_OPTIONS.map(c => <option key={c} value={c}>{conditionLabel(c)}</option>)}
                                </select>
                            </div>
                            <div className="col-md-4">
                                <label className="form-label fw-semibold">{t('assets.fieldPurchaseDate')}</label>
                                <input type="date" className="form-control" value={form.purchase_date} onChange={e => setForm({ ...form, purchase_date: e.target.value })} />
                            </div>
                            <div className="col-md-4">
                                <label className="form-label fw-semibold">{t('assets.fieldPurchaseCost', '', {currency})}</label>
                                <input type="number" className="form-control" step="0.01" value={form.purchase_cost} onChange={e => setForm({ ...form, purchase_cost: e.target.value })} />
                            </div>
                            <div className="col-md-4">
                                <label className="form-label fw-semibold">{t('assets.fieldCurrentValue', '', {currency})}</label>
                                <input type="number" className="form-control" step="0.01" value={form.current_value} onChange={e => setForm({ ...form, current_value: e.target.value })} />
                            </div>
                            <div className="col-md-6">
                                <label className="form-label fw-semibold">{t('assets.fieldWarrantyExpiry')}</label>
                                <input type="date" className="form-control" value={form.warranty_expiry} onChange={e => setForm({ ...form, warranty_expiry: e.target.value })} />
                            </div>
                            <div className="col-12">
                                <label className="form-label fw-semibold">{t('assets.fieldNotes')}</label>
                                <textarea className="form-control" rows={2} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} />
                            </div>
                        </div>
                        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
                            <button type="button" className="btn btn-light" onClick={() => setShowModal(false)}>{t('assets.cancel')}</button>
                            <button type="submit" className="btn btn-primary" disabled={saving}>
                                {saving ? <><span className="spinner-border spinner-border-sm me-2" />{t('assets.saving')}</> : t('assets.saveAsset')}
                            </button>
                        </div>
                    </form>
                </Modal>
            )}
            {showImport && (
                <Modal title={t('assets.importTitle')} onClose={() => setShowImport(false)}>
                    <form onSubmit={runImport}>
                        <p style={{ fontSize: 13, color: '#475569' }}>{t('assets.importHelp')}</p>
                        <button type="button" className="btn btn-outline-secondary btn-sm mb-3" onClick={downloadTemplate}>
                            <MdFileDownload /> {t('assets.importTemplate')}
                        </button>
                        <input type="file" className="form-control mb-3" accept=".csv,.xlsx"
                            onChange={e => { setImportFile(e.target.files[0] || null); setImportResult(null); }} />
                        {importResult?.ok && (
                            <div className="alert alert-success py-2" style={{ fontSize: 13 }}>
                                {t('assets.importDone', '', { n: importResult.count })}
                                {importResult.tags_generated > 0 && <div>{t('assets.importTagsGenerated', '', { n: importResult.tags_generated })}</div>}
                            </div>
                        )}
                        {importResult?.error && (
                            <div className="alert alert-danger py-2" style={{ fontSize: 13 }}>
                                <div style={{ fontWeight: 600 }}>{importResult.error}</div>
                                {(importResult.errors || []).length > 0 && (
                                    <ul style={{ margin: '8px 0 0', paddingLeft: 18, maxHeight: 200, overflowY: 'auto' }}>
                                        {importResult.errors.map(item => (
                                            <li key={item.row}>{t('assets.importRow')} {item.row}{item.name ? ` (${item.name})` : ''}: {item.problems.join('; ')}</li>
                                        ))}
                                        {importResult.error_count > importResult.errors.length && (
                                            <li>{t('assets.importMoreErrors', '', { n: importResult.error_count - importResult.errors.length })}</li>
                                        )}
                                    </ul>
                                )}
                            </div>
                        )}
                        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                            <button type="button" className="btn btn-light" onClick={() => setShowImport(false)}>{t('assets.cancel')}</button>
                            <button type="submit" className="btn btn-primary" disabled={importBusy || !importFile}>
                                {importBusy ? <><span className="spinner-border spinner-border-sm me-2" />{t('assets.importing')}</> : t('assets.importStart')}
                            </button>
                        </div>
                    </form>
                </Modal>
            )}
        </div>
    );
}
